import { z } from 'zod'
import { treeWorkChoiceSchema } from '../enums/tree-work-choice.enum.js'
import { MONEY_AMOUNT_PATTERN } from '../helpers/money.js'
import { proposalPlanSchema } from '../enums/proposal-plan.enum.js'
import {
  jobTypeSchema,
  type DeclinableJobType,
} from '../enums/job-type.enum.js'

/**
 * A client's choices, submitted from the shared proposal page.
 *
 * Choices only — never a price. What a browser sends is a set of things
 * somebody declined; the figure that follows is computed by the API from the
 * frozen document, because a total that arrived from the client would be a
 * total the client could set.
 *
 * Names rather than ids throughout, matching the way the document snapshots
 * species and problems: the selection is made against a frozen document, and
 * an id would let a rename in the app change what was agreed.
 */
export const acceptProposalInputSchema = z
  .object({
    excludedSites: z.array(z.string().trim().min(1).max(200)).max(100).default([]),
    excludedSpecies: z
      .array(z.string().trim().min(1).max(200))
      .max(100)
      .default([]),
    /**
     * OTHER is rejected rather than ignored. Access, setup and clearing up
     * have to happen for the crew to be on the property at all, so a request
     * to drop them is a request the app never offered — and answering it
     * quietly would leave somebody believing they had declined it.
     */
    excludedJobTypes: z
      .array(
        jobTypeSchema.refine(
          (type): type is DeclinableJobType => type !== 'OTHER',
          'That work cannot be declined',
        ),
      )
      .max(20)
      .default([]),
    excludedProblems: z
      .array(z.string().trim().min(1).max(200))
      .max(100)
      .default([]),
    /**
     * Per-tree choices, only where they differ from what was offered.
     *
     * Capped generously: a ninety-tree proposal could in principle have a
     * decision on every one, though in practice only trees quoted for removal
     * have a choice to make.
     */
    treeChoices: z
      .array(
        z
          .object({
            site: z.string().trim().min(1).max(200),
            label: z.string().trim().min(1).max(12),
            choice: treeWorkChoiceSchema,
          })
          .strict(),
      )
      .max(500)
      .default([]),
    /**
     * Trees switched off one by one. Capped past the largest bid we have
     * seen: a two-hundred-tree proposal can leave every one of them out.
     */
    excludedTrees: z
      .array(
        z
          .object({
            site: z.string().trim().min(1).max(200),
            label: z.string().trim().min(1).max(12),
          })
          .strict(),
      )
      .max(2000)
      .default([]),
    excludedAreas: z
      .array(
        z
          .object({
            site: z.string().trim().min(1).max(200),
            areaId: z.string().trim().min(1).max(200),
          })
          .strict(),
      )
      .max(500)
      .default([]),
    excludedAreaNames: z
      .array(
        z
          .object({
            site: z.string().trim().min(1).max(200),
            name: z.string().trim().min(1).max(80),
          })
          .strict(),
      )
      .max(500)
      .default([]),
    /*
     * Irrigation: all of it, a kind of repair, a zone, or a single repair
     * (which is how a plan scopes it). Defaulted, so a page built before
     * irrigation could be bid still sends a payload this accepts.
     */
    excludedIrrigation: z.boolean().default(false),
    excludedIrrigationKinds: z
      .array(z.string().trim().min(1).max(200))
      .max(100)
      .default([]),
    excludedZones: z
      .array(
        z
          .object({
            site: z.string().trim().min(1).max(200),
            zone: z.string().trim().min(1).max(120),
          })
          .strict(),
      )
      .max(500)
      .default([]),
    excludedRepairs: z
      .array(
        z
          .object({
            site: z.string().trim().min(1).max(200),
            key: z.string().trim().min(1).max(64),
          })
          .strict(),
      )
      .max(2000)
      .default([]),
    /**
     * The plan the client started from, recorded so the trees a two-year
     * plan leaves for next year read as planned rather than declined. The
     * price still comes from the choices above, never from this.
     *
     * Null when they adjusted from nothing in particular, and on any caller
     * written before plans existed.
     */
    plan: proposalPlanSchema.nullable().default(null),
    /**
     * The budget they typed, in dollars, when the plan was fitted to one.
     * Kept as a sales signal ("they have $9,000 this year"); it prices
     * nothing.
     */
    budgetAmount: z
      .string()
      .trim()
      .regex(MONEY_AMOUNT_PATTERN, 'Enter a budget in dollars, e.g. 9000')
      .nullable()
      .default(null),
  })
  .strict()

export type AcceptProposalInput = z.infer<typeof acceptProposalInputSchema>
